/**
 * `useNamingFlowStore` — Zustand wrapper around the pure Naming_Flow
 * reducer (`reduceNaming`) and per-language matchers
 * (`classifyNamingResponse`).
 *
 * The pure core is intentionally untouched: this slice merely owns the
 * `flowState` reference, dispatches typed events, and triggers two
 * side effects that are out of scope for the pure reducer:
 *
 *   1. When the reducer transitions to `confirmed`, the slice
 *      auto-persists the chosen name through `useCompanionNameStore`
 *      and clears `isActive` so the Naming_Flow UI exits
 *      (Requirements 16.4 and 16.6).
 *
 *   2. When the End_User replies during the flow, `ingestUserReply`
 *      runs the classifier and dispatches the matching event. Mixed-
 *      language input falls through to the matcher's `ambiguous`
 *      branch (Requirement 16.12) which keeps the flow in `clarifying`
 *      until the user disambiguates.
 *
 * `isActive` mirrors the gate predicate (`isNamingFlowActive`) but is
 * kept as a separate field so React components can subscribe to it
 * directly without re-deriving from `flowState.phase`. The two never
 * disagree because both are written through the same code paths.
 *
 * Validates: Requirements 16.1, 16.4, 16.5, 16.6, 16.10, 16.12.
 */

import { create } from 'zustand';

import { isNamingFlowActive as gateActive } from '../domain/namingFlow/gate';
import { classifyNamingResponse } from '../domain/namingFlow/matcher';
import {
  initialNamingState,
  reduceNaming,
} from '../domain/namingFlow/reducer';
import type {
  NamingEvent,
  NamingState,
} from '../domain/namingFlow/types';
import type { LanguageCode } from '../domain/types';

import { useCompanionNameStore } from './useCompanionNameStore';

/** Public slice surface. */
export interface NamingFlowState {
  /** Current Naming_Flow state. Pure reducer return. */
  flowState: NamingState;
  /** `true` iff the flow is active (gate predicate). */
  isActive: boolean;
  /**
   * Initialize the slice from the persisted companion name. Call once
   * during boot, after `useCompanionNameStore.hydrate()` has resolved.
   */
  hydrate(persistedName: string | null): void;
  /** Dispatch a typed event into the pure reducer. */
  dispatch(event: NamingEvent): void;
  /** Convenience: dispatch `aiProposed` after the AI proposes a name. */
  aiProposed(name: string): void;
  /**
   * Convenience: classify a user reply and dispatch the matching
   * event. The classifier follows the priority order documented in
   * `matcher.ts` (custom > another > accepts > ambiguous).
   */
  ingestUserReply(message: string, language: LanguageCode): void;
  /**
   * Persist the confirmed name through `useCompanionNameStore` and
   * exit the flow. Called automatically when the reducer transitions
   * to `confirmed`; exported for tests and for callers that want to
   * short-circuit the dispatch path.
   */
  confirmAndPersist(name: string): Promise<void>;
}

export const useNamingFlowStore = create<NamingFlowState>((set, get) => ({
  flowState: { phase: 'idle' },
  isActive: false,

  hydrate(persistedName: string | null): void {
    set({
      flowState: initialNamingState(persistedName),
      isActive: gateActive(persistedName),
    });
  },

  dispatch(event: NamingEvent): void {
    const next = reduceNaming(get().flowState, event);
    set({ flowState: next });

    // The reducer is pure — the side effect of persisting the
    // confirmed name lives here so the slice owns it. The reducer
    // can produce `confirmed` from multiple events
    // (`userAccepts`, `userProvidesCustom`, even `userAccepts` from
    // the `clarifying` phase), so we observe the resulting state
    // rather than the originating event.
    if (next.phase === 'confirmed') {
      void get().confirmAndPersist(next.name);
    }

    // `wipeAll` re-arms the flow (Requirement 16.10). Mirror the
    // gate's "no name persisted" view by flipping `isActive` back on
    // when the reducer returns to `awaitingFirstReply`.
    if (event.kind === 'wipeAll') {
      set({ isActive: true });
    }
  },

  aiProposed(name: string): void {
    get().dispatch({ kind: 'aiProposed', name });
  },

  ingestUserReply(message: string, language: LanguageCode): void {
    const classification = classifyNamingResponse({ message, language });
    switch (classification.kind) {
      case 'accepts':
        get().dispatch({ kind: 'userAccepts' });
        return;
      case 'requestsAnother':
        get().dispatch({ kind: 'userRequestsAnother' });
        return;
      case 'providesCustom':
        get().dispatch({
          kind: 'userProvidesCustom',
          name: classification.name,
        });
        return;
      case 'ambiguous':
      default:
        get().dispatch({ kind: 'userAmbiguous' });
        return;
    }
  },

  async confirmAndPersist(name: string): Promise<void> {
    // One-way slice-to-slice call: the Naming_Flow's only side effect
    // on the rest of the app is "persist this name". The companion-
    // name slice handles its own persistence + change notification.
    await useCompanionNameStore.getState().setName(name);
    set({ isActive: false });
  },
}));
